import {
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tag,
  Text,
  useDisclosure,
  UseDisclosureProps,
} from '@chakra-ui/react';
import { DataTable, DataTableFilter, DataTableFilters } from '@pelicargo/datatable';
import { LineItemType, ServiceType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { getPrettyAircraftType, getPrettyVariantType, prettyCurrency, prettyDate } from '@pelicargo/utils';
import { createColumnHelper } from '@tanstack/react-table';
import { isAfter, startOfDay } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { HiCheck } from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';

import { trpc, TrpcOutputs } from '../config/trpc';
import { useSelectedQuoteVariant } from '../hooks/useSelectedQuoteVariant';
import { API } from '../utils/apiTypes';
import { AirportStops } from './AirportStops';

type Props = UseDisclosureProps & {
  quote: TrpcOutputs['getQuote'] | API.QuoteWithTotalCost;
};

type TableColumns = Partial<{
  select: boolean;
  id: number;
  departs_at: Date;
  arrives_at: Date;
  quote_id: number;
  type: TrpcOutputs['getAlternativeFlights'][0]['type'];
  flight_path: TrpcOutputs['getAlternativeFlights'][0]['flight_path'];
  aircraft?: undefined; // We fill in via flight_path

  rate: number; // In cents
  rate_type: TrpcOutputs['getAlternativeFlights'][0]['rate_type'];
}>;

export const QuoteScheduleModal = ({ quote, ...discloserProps }: Props) => {
  const { selectedQuoteVariantId, setSelectedQuoteVariantId } = useSelectedQuoteVariant();
  const selectedVariant = useMemo(() => {
    return quote?.quote_variants?.find((variant) => variant.id === selectedQuoteVariantId);
  }, [quote?.quote_variants, selectedQuoteVariantId]);
  const originTimezone = quote?.flight_path?.[0]?.origin_airport?.time_zone;
  const destinationTimezone = quote?.flight_path?.slice(-1)?.[0]?.destination_airport?.time_zone;
  const navigate = useNavigate();

  const { data } = trpc.getAlternativeFlights.useQuery(
    { request_id: quote?.request_id, provider_id: quote?.provider_id },
    { enabled: Boolean(quote?.request_id && quote?.provider_id) },
  );

  const count = data?.length;
  const scheduleDisclosure = useDisclosure(discloserProps);

  const onRowClick = useCallback(
    (row: TableColumns) => {
      const doesVariantBelongToQuote = quote?.quote_variants?.some((variant) => variant.id === row.id);
      // In the awful world that we call "single page applications", navigate just changes page state, and the value persists.
      // Thus we must always set it
      setSelectedQuoteVariantId(row.id);

      // If the variant does not belong to the quote, navigate to the proper quote quote variant page
      if (!doesVariantBelongToQuote) {
        navigate(`/requests/${quote?.request_id}/quotes/${row.quote_id}/${row.id}`);
        scheduleDisclosure.onClose();
        return;
      }

      scheduleDisclosure.onClose();
    },
    [navigate, quote?.quote_variants, quote?.request_id, scheduleDisclosure, setSelectedQuoteVariantId],
  );

  const columnHelper = createColumnHelper<TableColumns>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('select', {
        header: '',
        cell: (info) => (
          <Show if={selectedQuoteVariantId === Number(info.row.original.id)}>
            <Icon as={HiCheck} />
          </Show>
        ),
      }),
      columnHelper.accessor('departs_at', {
        header: 'Departure Date',
        cell: (info) => {
          return (
            <Flex alignItems="center" gap="0.5em">
              {prettyDate(info.getValue(), { timeZone: originTimezone, showTime: true })}
            </Flex>
          );
        },
        enableSorting: true,
        sortingFn: 'datetime',
      }),
      columnHelper.accessor('arrives_at', {
        header: 'Arrives',
        cell: (info) => {
          return (
            <Flex alignItems="center" gap="0.5em">
              {prettyDate(info.getValue(), { timeZone: destinationTimezone, showTime: true })}
            </Flex>
          );
        },
        enableSorting: true,
      }),
      columnHelper.accessor('flight_path', {
        header: 'Flight Path',
        cell: (info) => {
          return <AirportStops flightSegments={info.getValue()} w="full" />;
        },
      }),
      columnHelper.accessor('aircraft', {
        header: 'Aircraft Type',
        cell: (info) => {
          return info.row.original.flight_path
            .map((node) => getPrettyAircraftType(node.aircraft_type))
            ?.filter(Boolean)
            ?.join(', ');
        },
      }),
      columnHelper.accessor('type', {
        header: 'Service',
        cell: (info) => {
          const data = info.getValue();
          const label = getPrettyVariantType(data);

          return (
            <Flex alignItems="center" gap="0.5em">
              {label}
            </Flex>
          );
        },
      }),
      columnHelper.accessor('rate', {
        header: 'Rate',
        cell: (info) => {
          const rate = info.getValue();
          const rateType = info.row.original.rate_type;
          const isDeal = rateType === LineItemType.SPOT;

          return (
            <Stack direction="row" alignItems="center">
              <Text>{prettyCurrency(rate)}/kg</Text>
              <Show if={isDeal}>
                <Tag colorScheme="blue" size="sm">
                  Deal
                </Tag>
              </Show>
            </Stack>
          );
        },
      }),
    ],
    [columnHelper, selectedQuoteVariantId, originTimezone],
  );

  const [filters, setFilters] = useState<DataTableFilter[]>([
    {
      name: 'departs_at',
      label: 'Departs',
      value: new Date(),
      operator: 'gte',
      mode: 'insensitive',
      showOperator: false,
      inputType: 'date',
      type: 'date',
    },
    {
      name: 'type',
      label: 'Type',
      operator: 'in',
      value: [selectedVariant?.type],
      options: Object.values(ServiceType).map((type) => ({ label: type, value: type })),
      mode: 'insensitive',
      showOperator: false,
      inputType: 'checkbox',
      type: 'string',
    },
  ]);

  const filteredData = useMemo(() => {
    if (!data || !filters.length) return data ?? [];

    const filtered = data.filter((row) => {
      return filters.every((filter) => {
        const { name, operator, value } = filter;
        const rowValue = row?.[name];

        if (operator === 'in') {
          return Array.isArray(value) && [...value].includes(rowValue);
        } else if (operator === 'gte') {
          return isAfter(rowValue, startOfDay(new Date(value)));
        }
      });
    });

    return filtered;
  }, [data, filters]);

  return (
    <Modal {...scheduleDisclosure} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex justifyContent="space-between" pr="2em">
            <Flex>Alternative Schedules</Flex>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody h="full">
          <Stack spacing="4" h="full">
            <DataTableFilters filters={filters} setFilters={setFilters} isClearable={false} />
            <DataTable
              columns={columns}
              data={filteredData}
              dataCount={count ?? 0}
              columnVisibility={{ id: false }}
              onRowClick={onRowClick}
              hidePagination
              sorting={[
                { id: 'departs_at', desc: false },
                { id: 'arrives_at', desc: false },
              ]}
              manualSorting={false}
            />
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
